import { console_api } from '../http/consoleClient';
import { toBase64 } from '../encoding';
import {
  DashboardSqlValidationResult,
  RuntimeQuestionReviewItem,
  TrainingEntityType,
  TrainingGenerateQuestionResult,
  TrainingGenerateSqlResult,
  TrainingProgressResponse,
  TrainingQuestionCreateResult,
  TrainingQuestionListItem,
} from 'types/console/training.types';

export const consoleTrainingApi = {
  submitTrainingQuestion: async (payload: {
    requester_email: string;
    entity_type: TrainingEntityType;
    entity_id: string;
    question: string;
    sql_text?: string;
    sql_source: 'manual' | 'generated';
    entity_mapping_confirmed: boolean;
    sql_validated: boolean;
    is_100_correct: boolean;
    source: string;
    override_duplicate: boolean;
    approved_by_email?: string;
  }): Promise<TrainingQuestionCreateResult> => {
    const response = await console_api.post<TrainingQuestionCreateResult>('/console/training/questions', {
      ...payload,
      sql_text_base64: payload.sql_text ? toBase64(payload.sql_text) : undefined,
    });
    return response.data;
  },

  listTrainingQuestions: async (payload: {
    requester_email: string;
    entity_type: TrainingEntityType;
    entity_id: string;
    limit?: number;
  }): Promise<TrainingQuestionListItem[]> => {
    const response = await console_api.get<{ questions: TrainingQuestionListItem[] }>('/console/training/questions', {
      params: {
        requester_email: payload.requester_email,
        entity_type: payload.entity_type,
        entity_id: payload.entity_id,
        limit: payload.limit || 300,
      },
    });
    return response.data.questions;
  },

  approveTrainingQuestionByMainDeveloper: async (payload: {
    requester_email: string;
    question_id: string;
    is_approved: boolean;
    approval_note: string;
  }): Promise<void> => {
    await console_api.patch(`/console/training/questions/${encodeURIComponent(payload.question_id)}/main-approval`, {
      requester_email: payload.requester_email,
      is_approved: payload.is_approved,
      approval_note: payload.approval_note,
    });
  },

  generateTrainingSql: async (payload: {
    requester_email: string;
    entity_type: TrainingEntityType;
    entity_id: string;
    question: string;
    max_rows?: number;
  }): Promise<TrainingGenerateSqlResult> => {
    const response = await console_api.post<TrainingGenerateSqlResult>('/console/training/generate-sql', {
      requester_email: payload.requester_email,
      entity_type: payload.entity_type,
      entity_id: payload.entity_id,
      question: payload.question,
      max_rows: payload.max_rows || 2000,
    });
    return response.data;
  },

  deleteTrainingQuestion: async (questionId: string, requesterEmail: string): Promise<void> => {
    await console_api.delete(`/console/training/questions/${questionId}`, {
      data: { requester_email: requesterEmail },
    } as any);
  },

  generateTrainingQuestion: async (payload: {
    requester_email: string;
    entity_type: TrainingEntityType;
    entity_id: string;
  }): Promise<TrainingGenerateQuestionResult> => {
    const response = await console_api.post<TrainingGenerateQuestionResult>('/console/training/generate-question', {
      requester_email: payload.requester_email,
      entity_type: payload.entity_type,
      entity_id: payload.entity_id,
    });
    return response.data;
  },

  validateTrainingSql: async (payload: {
    requester_email: string;
    entity_type: TrainingEntityType;
    entity_id: string;
    sql: string;
  }): Promise<DashboardSqlValidationResult> => {
    const response = await console_api.post<DashboardSqlValidationResult>('/console/training/validate-sql', {
      requester_email: payload.requester_email,
      entity_type: payload.entity_type,
      entity_id: payload.entity_id,
      sql_base64: toBase64(payload.sql),
    });
    return response.data;
  },

  getTrainingProgress: async (
    entityType: TrainingEntityType,
    entityId: string,
    requesterEmail: string
  ): Promise<TrainingProgressResponse> => {
    const response = await console_api.get<TrainingProgressResponse>('/console/training/progress', {
      params: {
        entity_type: entityType,
        entity_id: entityId,
        requester_email: requesterEmail,
      },
    });
    return response.data;
  },

  getRuntimeReviews: async (requesterEmail: string, limit = 100): Promise<RuntimeQuestionReviewItem[]> => {
    const response = await console_api.get<{ reviews: RuntimeQuestionReviewItem[] }>('/console/training/runtime-reviews', {
      params: {
        requester_email: requesterEmail,
        limit,
      },
    });
    return response.data.reviews;
  },

  updateRuntimeReview: async (payload: {
    requester_email: string;
    review_id: string;
    is_answer_correct: boolean;
    is_source_correct: boolean;
    expected_entity_type: TrainingEntityType;
    expected_entity_id: string;
    improvement_note: string;
  }): Promise<void> => {
    await console_api.patch(`/console/training/runtime-reviews/${encodeURIComponent(payload.review_id)}`, {
      requester_email: payload.requester_email,
      is_answer_correct: payload.is_answer_correct,
      is_source_correct: payload.is_source_correct,
      expected_entity_type: payload.expected_entity_type,
      expected_entity_id: payload.expected_entity_id,
      improvement_note: payload.improvement_note,
    });
  },
};
